
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
  RatingEntity,
  RatingExtraFieldConfig,
  RatingFieldKey,
  RatingMode,
  RatingQuestionConfig,
  RatingSurveyConfig
} from '@/entity/RatingEntity';
import { ProductEnum } from '@/enum/ProductEnum';

@Component
export default class RatingDialog extends Vue {
  @Prop({ default: 'general' }) mode!: RatingMode;
  @Prop({ default: false }) value!: boolean;

  localValue: RatingEntity = new RatingEntity();
  surveyConfig: RatingSurveyConfig | null = null;
  surveyConfigs: RatingSurveyConfig[] = [];
  currentSurveyIndex: number = 0;

  get dialog(): boolean {
    return this.value;
  }

  set dialog(val: boolean) {
    this.$emit('input', val);
  }

  get title(): string {
    return this.surveyConfig ? this.surveyConfig.title : '';
  }

  get isValid(): boolean {
    if (!this.surveyConfig) return false;

    for (const question of this.surveyConfig.questions) {
      if (!this.getRatingNumber(question.key)) return false;
      if (this.shouldShowReason(question) && question.reason_required && !this.getTextValue(question.reason_key).trim()) return false;
    }

    for (const field of this.extraFields) {
      if (field.required && !this.getTextValue(field.key).trim()) return false;
    }

    return true;
  }

  get extraFields(): RatingExtraFieldConfig[] {
    if (!this.surveyConfig) return [];
    return Array.isArray(this.surveyConfig.extra_fields) ? this.surveyConfig.extra_fields : [];
  }

  get extraFieldObject() {
    if (!this.surveyConfig) return {};
    return !Array.isArray(this.surveyConfig.extra_fields) ? this.surveyConfig.extra_fields : {};
  }

  get showLinkedin(): boolean {
    return this.mode === 'general' && this.extraFieldObject.show_linkedin === true;
  }

  get linkedinTitle(): string {
    return this.extraFieldObject.linkedin_title || '';
  }

  get linkedinUrl(): string {
    return this.extraFieldObject.linkedin_url || '';
  }

  get linkedinLabel(): string {
    return this.extraFieldObject.linkedin_label || '';
  }

  mounted() {
    if (this.value) {
      this.loadSurveyConfig();
    }
  }

  @Watch('value')
  onDialogValueChange(value: boolean) {
    if (value) {
      this.loadSurveyConfig();
    }
  }

  @Watch('mode')
  onModeChange() {
    this.surveyConfig = null;
    if (this.value) {
      this.loadSurveyConfig();
    }
  }

  closeDialog() {
    this.dialog = false;
    this.resetForm();
    this.currentSurveyIndex = 0;
  }

  /**
   * "Daha Sonra Hatırlat" ve çarpı (X) butonu icin ortak davranis: anketi kapat ve
   * ust bilesene (App.vue) bildir. App.vue ilgili modu belirlenen sure (1 gun)
   * boyunca tekrar gostermez. Boylece kullanici ekran degistirdikce anket geri gelmez.
   */
  remindLater() {
    this.$emit('remind-later', this.mode);
    this.closeDialog();
  }

  async loadSurveyConfig() {
    this.surveyConfig = null;
    this.surveyConfigs = [];
    this.currentSurveyIndex = 0;

    try {
      const productId = this.productIdForMode();
      const configs = await this.fetchSurveyPreview(productId);
      const activeConfigs = configs.filter(config => Number(config.active) !== 0);
      const visibleConfigs = await this.filterVisibleConfigs(activeConfigs, productId);
      if (!visibleConfigs.length) {
        this.closeDialog();
        return;
      }
      this.surveyConfigs = visibleConfigs;
      this.surveyConfig = visibleConfigs[0];
    } catch (error) {
      console.error('Anket ayarları alınırken hata oluştu:', error);
      this.closeDialog();
    }
  }

  async fetchSurveyPreview(productId: number): Promise<RatingSurveyConfig[]> {
    const cloudBaseUrl = process.env.VUE_APP_ZIRVE_CLOUD_URL || 'https://zirve-bilgisayar.com';
    const response = await this.$http.post(`${cloudBaseUrl}/cloud/degerlendirme`, {
      preview: true,
      mode: this.mode,
      product_id: productId
    });

    const configList = Array.isArray(response) ? response : (Array.isArray(response?.anketler) ? response.anketler : []);
    return this.normalizeConfigList(configList);
  }

  normalizeConfigList(response: RatingSurveyConfig[] | Partial<RatingSurveyConfig>): RatingSurveyConfig[] {
    const configs = Array.isArray(response) ? response : [response];
    return configs
        .filter(config => !!config && !!config.mode && !!config.product_id)
        .map(config => this.normalizeConfig(config));
  }

  normalizeConfig(config: Partial<RatingSurveyConfig>): RatingSurveyConfig {
    return {
      id: config.id,
      mode: config.mode || this.mode,
      product_id: Number(config.product_id || this.productIdForMode()),
      title: config.title || '',
      description: config.description || null,
      remind_later_text: config.remind_later_text || '',
      submit_text: config.submit_text || '',
      active: Number(config.active || 0),
      sort_order: Number(config.sort_order || 1),
      questions: Array.isArray(config.questions) && config.questions.length ? config.questions.map(question => ({
        ...question,
        max_score: Number(question.max_score || 10),
        low_score_threshold: Number(question.low_score_threshold || 0),
        reason_required: Boolean(question.reason_required)
      })) : [],
      extra_fields: config.extra_fields !== undefined ? config.extra_fields : []
    };
  }

  async filterVisibleConfigs(configs: RatingSurveyConfig[], productId: number): Promise<RatingSurveyConfig[]> {
    if (!configs.length) return [];

    try {
      const visibilityResponse = await this.$http.get(`/api/v1/anket-visibility?product_id=${productId}`);
      const visibilityList = Array.isArray(visibilityResponse) ? visibilityResponse : [];
      const visibilityByAnketId = visibilityList.reduce((map: Record<number, boolean>, item: any) => {
        map[Number(item.anket_id)] = item.visible !== false && Number(item.visible) !== 0;
        return map;
      }, {});

      return configs.filter(config => !config.id || visibilityByAnketId[config.id] !== false);
    } catch (error) {
      console.error('Anket görünürlük bilgisi alınırken hata oluştu:', error);
      return configs;
    }
  }

  productIdForMode(): ProductEnum {
    return this.mode === 'general' ? ProductEnum.ICRAPROWEB : ProductEnum.EICRAPRO;
  }

  getScoreRange(question: RatingQuestionConfig): number[] {
    const maxScore = Number(question.max_score || 10);
    return Array.from({ length: maxScore }, (_value, index) => index + 1);
  }

  getRatingNumber(key: RatingFieldKey): number {
    return Number(this.localValue[key] || 0);
  }

  setRatingValue(key: RatingFieldKey, value: number) {
    this.$set(this.localValue, key, value);
  }

  getTextValue(key: RatingFieldKey): string {
    return String(this.localValue[key] || '');
  }

  setTextValue(key: RatingFieldKey, value: string) {
    this.$set(this.localValue, key, value);
  }

  shouldShowReason(question: RatingQuestionConfig): boolean {
    const rating = this.getRatingNumber(question.key);
    return rating > 0 && rating <= Number(question.low_score_threshold || 0);
  }

  /**
   * Anketi dolduran alt kullanicinin bagli oldugu ana hesap/buro bilgisini,
   * login sonrasi store'da zaten cache'li olan user objesinden okuyup payload'a
   * ekler. Yeni HTTP istegi YAPMAZ. Panel anket sonuc ekraninda "hangi buroya
   * ait" bilgisinin gosterilmesi icin kullanilir.
   */
  fillAnaHesapBilgisi() {
    try {
      const user: any = this.$store?.state?.user || {};
      const activeTenantId: string | null = this.$store?.state?.tenant_id || null;

      // Buro / firma adi
      this.localValue.buro_adi = (user.buro && user.buro.isim) ? String(user.buro.isim) : null;

      // Ana hesap sahibi adi (kurum ise firma, sahis ise kisi adi).
      this.localValue.ana_hesap_sahibi = user.name
        ? String(user.name)
        : [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || null;

      // Aktif tenant'a ait lisans (tenant_id + lisans no).
      const licenses: any[] = Array.isArray(user.licenses) ? user.licenses : [];
      const lisans = licenses.find((l: any) => String(l.tenant_id) === String(activeTenantId)) || licenses[0] || null;

      this.localValue.tenant_id = lisans && lisans.tenant_id
        ? String(lisans.tenant_id)
        : (activeTenantId ? String(activeTenantId) : null);
      this.localValue.lisans_no = lisans && lisans.id != null ? String(lisans.id) : null;
    } catch (e) {
      // Ana hesap bilgisi cikarilamazsa anket gonderimi engellenmesin.
      console.warn('Ana hesap bilgisi doldurulamadi:', e);
    }
  }

  async saveRating() {
    if (this.surveyConfig && this.isValid) {
      try {
        this.localValue.product_id = this.surveyConfig.product_id || this.productIdForMode();
        if (this.surveyConfig.id) {
          this.localValue.anket_id = this.surveyConfig.id;
        }

        this.fillAnaHesapBilgisi();

        const cloudBaseUrl = process.env.VUE_APP_ZIRVE_CLOUD_URL || 'https://zirve-bilgisayar.com';
        await this.$http.post(`${cloudBaseUrl}/cloud/degerlendirme`, this.localValue);
        await this.markSurveyInvisible(this.surveyConfig);

        if (this.hasNextSurvey()) {
          this.showNextSurvey();
          return;
        }

        this.$toast.success('Değerlendirme Sonucunuz Gönderildi');
        this.closeDialog();
      } catch (error) {
        console.error('Puanlama kaydedilirken hata oluştu:', error);
      }
    }
  }

  async markSurveyInvisible(config: RatingSurveyConfig): Promise<void> {
    if (!config.id) return;

    await this.$http.patch(`/api/v1/anket/${config.id}/visibility`, {
      visible: false,
      product_id: config.product_id || this.productIdForMode()
    });
  }

  hasNextSurvey(): boolean {
    return this.currentSurveyIndex + 1 < this.surveyConfigs.length;
  }

  showNextSurvey() {
    this.currentSurveyIndex += 1;
    this.surveyConfig = this.surveyConfigs[this.currentSurveyIndex];
    this.localValue = new RatingEntity();
  }

  resetForm() {
    setTimeout(() => {
      this.localValue = new RatingEntity();
    }, 300);
  }
}
